import React, { useEffect, useState } from 'react';
import { useProductsContext } from '../../contexts/ProductsContext';
import {
  AddToCartButton,
  AnimatedCheckmark,
  Input,
  MoneyFormat,
  OfferPrice,
  OfferQuantity,
  RemoveButton,
  Title,
} from '../elements';
import {
  AddButtonAction,
  CandyRackSettings,
  isGridLayout,
  Offer,
  popupLayoutCssPostfix,
  Product,
  Settings,
} from '../../types';
import Icon from '../../icons';
import { OfferBody } from '../OfferBody';
import ProductDetail from '../ProductDetail';
import useAnimation from '../../hooks/useAnimation';
import { BACKEND_MAX_VALUE } from '../elements/OfferQuantity';
import { useSharedContext } from '../../contexts/SharedContext';
import { REGISTERED_APPS } from '../../shared/types/Apps';
import { getAddedOffers } from '../../utils/candycart/products';
import { usePopupContext } from '../../contexts/Settings';
import cn from 'classnames';

interface Props {
  offer: Offer;
  currency: string;
  indexInOffers?: number;
  handleAddToCart: (
    offer: Offer,
    product?: Product,
    action?: AddButtonAction,
  ) => Promise<boolean>;
  offerCount: number;
  handleRemoveFromCart: (offer: Offer, variant?: number) => Promise<void>;
}

function createOfferImageClasses(settings: Settings): string {
  if (isGridLayout(settings)) {
    return '';
  } else {
    switch (settings.image_size) {
      case 'small':
        return 'offer-icon-container-small';
      case 'medium':
        return 'offer-icon-container-medium';
      case 'large':
      default:
        return 'offer-icon-container-large';
    }
  }
}

function createOfferIconClasses(settings: Settings): string {
  if (isGridLayout(settings)) {
    return 'offer-icon-grid';
  } else {
    switch (settings.image_size) {
      case 'small':
        return 'offer-icon-small';
      case 'medium':
        return 'offer-icon-medium';
      case 'large':
      default:
        return 'offer-icon-large';
    }
  }
}

const ServiceOffer = ({
  offer,
  handleAddToCart,
  handleRemoveFromCart,
  indexInOffers,
  offerCount,
}: Props) => {
  const { resetSelectedProduct, selectedProduct, setProductDetail } =
    useProductsContext();
  const { runningAppName } = useSharedContext();
  const candyRackSettings = usePopupContext().settings as CandyRackSettings;

  const [added, setAdded] = useState(false);
  const canPlayAnimation = useAnimation({ play: added });
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(
    Math.min(BACKEND_MAX_VALUE, offer.maxQuantityComputedInFE || 1),
  );
  const [offerNote, setOfferNote] = useState<string>('');

  const handleRemoveFromCartClick = async () => {
    setAdded(false);
    setLoading(true);
    await handleRemoveFromCart(
      offer,
      offer.shopifyProductVariantIdComputedInFE,
    );
    setLoading(false);
  };

  const handleAddToCartClick = async () => {
    resetSelectedProduct();
    setLoading(true);

    //For Add To Cart and Checkout Button we currently need to manipulate the original object
    // to show what is added, as the code still depends on the shopify_product_variant_id. As its
    // a service, we know that the new structure has only one item with one item variant
    if (offer.items?.length > 0 && offer.items[0].item_variants?.length > 0) {
      // Set offer properties
      offer.shopifyProductVariantIdComputedInFE =
        offer.items[0].item_variants[0];
    }

    offer.quantityComputedInFE = Math.min(BACKEND_MAX_VALUE, quantity);
    offer.noteComputedInFE = offerNote;

    const productAdded = await handleAddToCart(
      offer,
      undefined,
      candyRackSettings.buttons.add.action,
    );
    setAdded(productAdded);
    setLoading(false);
  };

  // Set already added for Candycart
  useEffect(() => {
    if (runningAppName === REGISTERED_APPS.CANDYCART) {
      const addedOffers = getAddedOffers();
      const addedOffer = addedOffers.find((o) => o.id === offer.id);
      if (addedOffer) {
        setQuantity(addedOffer.quantity);
        setAdded(true);
      } else {
        setAdded(false);
      }
    }
  }, [offer, runningAppName]);

  const { settings } = usePopupContext();

  useEffect(
    () => {
      if (selectedProduct?.productID === offer.id) {
        setProductDetail(
          <ProductDetail
            product={offer}
            offer={offer}
            buyContainer={
              <>
                {offer.show_note_field && (
                  <div className="Note-Field--wrapper">
                    <Input
                      className="md:w-250"
                      disabled={added}
                      defaultValue={offerNote}
                      onChange={(event) => setOfferNote(event.target.value)}
                    />
                  </div>
                )}
                <div className="flex items-stretch">
                  {offer.enable_quantity_selector && (
                    <OfferQuantity
                      quantity={quantity}
                      handleOfferQuantityChange={setQuantity}
                      disabled={added || loading}
                      containerClasses="mr-10"
                      inputClasses="Quantity-Selector-Limit-Width ml-4"
                    />
                  )}
                  <AddToCartButton
                    disabled={added || loading}
                    onClick={handleAddToCartClick}
                    data-cy="add"
                    data-accepted-product-variant={
                      added
                        ? offer.shopifyProductVariantIdComputedInFE
                        : undefined
                    }
                    className="Button-Add"
                  >
                    {added ? settings.text.added : settings.text.add}
                  </AddToCartButton>
                  {added ? (
                    <RemoveButton
                      disabled={loading}
                      onClick={handleRemoveFromCartClick}
                      className="Button-Remove"
                    />
                  ) : null}
                </div>
              </>
            }
          />,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedProduct,
      added,
      loading,
      offer,
      quantity,
      offerNote,
      settings.text.add,
      settings.text.added,
    ],
  );

  const popupLayoutCssPostfixValue: string = popupLayoutCssPostfix(settings);

  return (
    <OfferBody
      id={offer.id}
      offerCount={offerCount}
      indexInOffers={indexInOffers}
      hidden={!!selectedProduct}
      offer={offer}
      //cyData is also used as a simple distinction for when we are following the ServiceOffer path
      cyData="offer-service"
      img={
        <div
          className={cn(
            createOfferImageClasses(settings),
            'bg-grey-5 flex justify-center items-center object-scale-down',
          )}
        >
          <Icon
            className={createOfferIconClasses(settings)}
            name={offer.icon}
          />
        </div>
      }
      animatedIcon={
        added ? (
          <AnimatedCheckmark size="md" playAnimation={canPlayAnimation} />
        ) : null
      }
      loading={loading}
      added={added}
      handleRemoveFromCartClick={handleRemoveFromCartClick}
      primaryButton={
        <AddToCartButton
          disabled={added || loading}
          onClick={handleAddToCartClick}
          data-accepted-product-variant={
            added ? offer.shopifyProductVariantIdComputedInFE : undefined
          }
          data-cy="add"
          className={`Button-Add${popupLayoutCssPostfixValue} ${
            added ? `Button-Add-Added${popupLayoutCssPostfixValue}` : ''
          }`}
        >
          {added ? settings.text.added : settings.text.add}
        </AddToCartButton>
      }
      setOfferNote={setOfferNote}
      offerNote={offerNote}
      offerDescriptionHeading={
        <div className="Offer-Description-Heading">
          <Title clickable className="Offer-Title">
            <span className="font-normal text-grey-2 text-md">
              {quantity > 1 && added ? `${quantity} × ` : ''}
            </span>
            {offer.headline}
          </Title>
          <OfferPrice>
            {/* We need to use rate here, since the price comes from us. */}
            <MoneyFormat
              amount={
                Number(offer.price) * Number(window.Shopify.currency.rate)
              }
            />
          </OfferPrice>
        </div>
      }
      quantity={quantity}
      handleOfferQuantityChange={setQuantity}
      upsellListItemCustomClassName="full-width-icon-grid"
    />
  );
};

export default ServiceOffer;
